import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AccountBalance as TrustIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';
import { holdersApi } from '../services/api';
import { formatCurrency, getHolderTypeLabel, getCountryLabel } from '../utils/formatters';
import { useApp } from '../App';

const Holders = () => {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(true);
  const [holders, setHolders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editHolder, setEditHolder] = useState(null);
  const [detailHolder, setDetailHolder] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await holdersApi.getAll();
      setHolders(response.data);
    } catch (error) {
      showNotification('Ошибка загрузки владельцев', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (holder = null) => {
    setEditHolder(
      holder || {
        code: '',
        name: '',
        type: 'INDIVIDUAL',
        country: 'RU',
        description: '',
      }
    );
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editHolder.id) {
        await holdersApi.update(editHolder.id, editHolder);
        showNotification('Владелец обновлен', 'success');
      } else {
        await holdersApi.create(editHolder);
        showNotification('Владелец создан', 'success');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка сохранения', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить владельца?')) return;

    try {
      await holdersApi.delete(id);
      showNotification('Владелец удален', 'success');
      fetchData();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка удаления', 'error');
    }
  };

  const handleToggleActive = async (holder) => {
    try {
      if (holder.isActive) {
        await holdersApi.deactivate(holder.id);
        showNotification('Владелец деактивирован', 'success');
      } else {
        await holdersApi.activate(holder.id);
        showNotification('Владелец активирован', 'success');
      }
      fetchData();
    } catch (error) {
      showNotification('Ошибка изменения статуса', 'error');
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await holdersApi.getById(id);
      setDetailHolder(response.data);
    } catch (error) {
      showNotification('Ошибка загрузки деталей', 'error');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'INDIVIDUAL':
        return <PersonIcon />;
      case 'COMPANY':
        return <BusinessIcon />;
      case 'TRUST':
        return <TrustIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'INDIVIDUAL':
        return 'primary';
      case 'COMPANY':
        return 'secondary';
      case 'TRUST':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Владельцы
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Добавить владельца
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Holders List */}
        <Grid item xs={12} md={detailHolder ? 8 : 12}>
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Код</TableCell>
                    <TableCell>Название</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>Страна</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell width={150}>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Загрузка...
                      </TableCell>
                    </TableRow>
                  ) : holders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Нет владельцев
                      </TableCell>
                    </TableRow>
                  ) : (
                    holders.map((holder) => (
                      <TableRow
                        key={holder.id}
                        hover
                        onClick={() => handleViewDetails(holder.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: `${getTypeColor(holder.type)}.main` }}>
                              {getTypeIcon(holder.type)}
                            </Avatar>
                            <Typography fontWeight={600}>{holder.code}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{holder.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={getHolderTypeLabel(holder.type)}
                            size="small"
                            color={getTypeColor(holder.type)}
                          />
                        </TableCell>
                        <TableCell>{getCountryLabel(holder.country)}</TableCell>
                        <TableCell>
                          <Chip
                            icon={holder.isActive ? <ActiveIcon /> : <BlockIcon />}
                            label={holder.isActive ? 'Активен' : 'Неактивен'}
                            size="small"
                            color={holder.isActive ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Редактировать">
                            <IconButton size="small" onClick={() => handleOpenDialog(holder)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={holder.isActive ? 'Деактивировать' : 'Активировать'}>
                            <IconButton size="small" onClick={() => handleToggleActive(holder)}>
                              {holder.isActive ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Удалить">
                            <IconButton size="small" color="error" onClick={() => handleDelete(holder.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Detail Panel */}
        {detailHolder && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">{detailHolder.code}</Typography>
                  <IconButton size="small" onClick={() => setDetailHolder(null)}>
                    ×
                  </IconButton>
                </Box>
                <Typography variant="body1" gutterBottom>
                  {detailHolder.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={getHolderTypeLabel(detailHolder.type)}
                    size="small"
                    color={getTypeColor(detailHolder.type)}
                  />
                  <Chip label={getCountryLabel(detailHolder.country)} size="small" />
                </Box>

                {detailHolder.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {detailHolder.description}
                  </Typography>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Статистика
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Активов</TableCell>
                      <TableCell align="right">{detailHolder.stats?.assetCount || 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Общая стоимость</TableCell>
                      <TableCell align="right">
                        {formatCurrency(detailHolder.stats?.totalValue || 0, 'USD')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Счетов</TableCell>
                      <TableCell align="right">{detailHolder.stats?.accountCount || 0}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {detailHolder.assets?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Активы
                    </Typography>
                    <List dense>
                      {detailHolder.assets.slice(0, 5).map((asset) => (
                        <ListItem key={asset.id}>
                          <ListItemText
                            primary={asset.ticker}
                            secondary={asset.name}
                          />
                          <Typography variant="body2">
                            {formatCurrency(asset.quantity * asset.price, asset.currency)}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editHolder?.id ? 'Редактировать владельца' : 'Добавить владельца'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Код"
                value={editHolder?.code || ''}
                onChange={(e) => setEditHolder({ ...editHolder, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Название"
                value={editHolder?.name || ''}
                onChange={(e) => setEditHolder({ ...editHolder, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Тип</InputLabel>
                <Select
                  value={editHolder?.type || 'INDIVIDUAL'}
                  label="Тип"
                  onChange={(e) => setEditHolder({ ...editHolder, type: e.target.value })}
                >
                  <MenuItem value="INDIVIDUAL">Физ. лицо</MenuItem>
                  <MenuItem value="COMPANY">Компания</MenuItem>
                  <MenuItem value="TRUST">Траст</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Страна</InputLabel>
                <Select
                  value={editHolder?.country || 'RU'}
                  label="Страна"
                  onChange={(e) => setEditHolder({ ...editHolder, country: e.target.value })}
                >
                  <MenuItem value="RU">Россия</MenuItem>
                  <MenuItem value="UAE">ОАЭ</MenuItem>
                  <MenuItem value="CY">Кипр</MenuItem>
                  <MenuItem value="US">США</MenuItem>
                  <MenuItem value="Other">Другое</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Описание"
                value={editHolder?.description || ''}
                onChange={(e) => setEditHolder({ ...editHolder, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSave}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Holders;
